import { Box, Flex, Popover, PopoverContent, PopoverTrigger, Tooltip, useOutsideClick } from '@chakra-ui/react';
import { Code } from 'constants/keyboard';
import { useDebouncedCallback } from 'hooks/useDebounce';
import { CSSProperties, KeyboardEvent, ReactElement, useCallback, useMemo, useRef } from 'react';
import { normalizeNumber } from 'utils/number';

import { ToolbarNumberInput } from '../../Toolbar/ToolbarNumberInput';
import { PopoverWrapper } from '../PopoverWrapper';
import { NumberInputDropdownContent } from './NumberInputDropdownContent';
import { NumberInputStepper, StepperType } from './NumberInputStepper';

const DEBOUNCE_TIME_MS = 100;

/**
 * NUMBER INPUT POPOVER COMPONENT
 *
 * This component renders a number input with a dropdown and
 * +/- stepper buttons.
 *
 * Trigger the `onChange` prop when user selects a number via the dropdown
 * or enters a value in the input field.
 *
 * If `onIncrement` or `onDecrement` props are defined, they will be triggered
 * when the +/- buttons are clicked.
 * Otherwise, `onChange` will be triggered with `value +/- 1`
 *
 */
interface NumberInputPopoverProps {
  value: number;
  // additional text to attach to the number
  suffix?: string;

  // List of numbers to be displayed in the dropdown
  dropdownOptions: (string | number)[];

  // Labels for tooltips
  label: string;
  incrementLabel?: string;
  incrementShortCutLabel?: ReactElement;
  decrementLabel?: string;
  decrementShortCutLabel?: ReactElement;

  inputId?: string;
  inputTestId?: string;

  min?: number;
  max?: number;

  // Number of decimal places to round to
  precision?: number;

  onChange?: (value: number) => void;

  /**
   * If these fns are passed in, when clicking the increment/decrement
   * buttons, these functions will be called instead of `onChange`
   *
   * If these are NOT passed in, `onChange` will be triggered
   * with the new value set to `value +/- 1`
   */
  onIncrement?: () => void;
  onDecrement?: () => void;

  triggerBlurOnUnmount?: boolean;
  showInputStepper?: boolean;

  popOverStyleProps?: CSSProperties;
  stepperBtnStyles?: {
    width?: number;
    // property to control if stepper button doesn't need border
    noBorder?: boolean;
  };
  // property to allow for string option but return an integer string for handlers
  // use string for optionValue instead of number to allow zeros
  stringValueOption?: {
    optionText?: string;
    optionValue?: string;
  };
  // if true the dropdown will display "--"
  isMultipleFontSize?: boolean;
}

export const NumberInputPopover = ({
  value,
  suffix = '',
  dropdownOptions,
  label,
  incrementLabel,
  incrementShortCutLabel,
  decrementLabel,
  decrementShortCutLabel,
  onIncrement,
  onDecrement,
  inputId,
  inputTestId,
  triggerBlurOnUnmount = true,
  min = 0,
  max = 100,
  precision = 0,
  onChange = () => {},
  showInputStepper = true,
  popOverStyleProps = {},
  stepperBtnStyles = {},
  stringValueOption = {},
  isMultipleFontSize = false,
}: NumberInputPopoverProps) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const selectedIndex = useMemo(() => dropdownOptions.findIndex((opt) => opt === value), [dropdownOptions, value]);

  const closeDropdown = useCallback(() => {
    const currActiveElement = document.activeElement;
    contentRef.current?.focus();
    contentRef.current?.blur();

    (currActiveElement as HTMLElement)?.focus();
  }, []);

  // Click outside should close dropdown
  // Can't rely on default Popover behaviour because the trigger is an <input /> element
  useOutsideClick({
    ref: dropdownRef,
    handler: closeDropdown,
  });

  const handleIncrement = useCallback(() => {
    if (typeof onIncrement === 'function') {
      onIncrement();
      return;
    }

    onChange?.(normalizeNumber(min, max, value + 1));
  }, [value, onChange, onIncrement, min, max]);

  const handleDecrement = useCallback(() => {
    if (typeof onDecrement === 'function') {
      onDecrement();
      return;
    }

    onChange?.(normalizeNumber(min, max, value - 1));
  }, [value, onChange, onDecrement, min, max]);

  const handleSelect = (e: React.SyntheticEvent<HTMLButtonElement>): void => {
    const value = e.currentTarget.dataset.value!;
    let newValue = parseInt(e.currentTarget.dataset.value!);

    // check if there's any option defined as string and if there's any number equivalent defined for onChange value
    const { optionText, optionValue } = stringValueOption;

    // check if a text or string option is defined in props
    if (optionText && optionValue) {
      // then check if the option currently selected and return the value parsed
      if (optionText === value) newValue = parseInt(optionValue);
    }

    // Don't call onSelect for non-changed values.
    if (
      selectedIndex ===
      dropdownOptions.findIndex((option) => {
        // if value returned is a string type, compare with optionText defined by stringValueOption props
        if (typeof option === 'string' && optionText) return optionText === option;
        return option === newValue;
      })
    ) {
      return;
    }

    onChange?.(newValue);
  };

  const handleChange = useCallback(
    (newValue: number) => {
      if (newValue === value) return;
      closeDropdown();

      onChange?.(newValue);
    },
    [value, onChange, closeDropdown],
  );

  const debouncedIncrement = useDebouncedCallback(handleIncrement, DEBOUNCE_TIME_MS);
  const debouncedDecrement = useDebouncedCallback(handleDecrement, DEBOUNCE_TIME_MS);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>, isMultipleFontSize?: boolean) => {
      if (e.code === Code.Tab) {
        contentRef.current?.focus();
      }

      // only allow toggled increment/decremenet workaround if it is a multiwidget selection
      if (!isMultipleFontSize) return;

      if (e.code === Code.ArrowUp) {
        debouncedIncrement?.();
        e.preventDefault();
      }

      if (e.code === Code.ArrowDown) {
        debouncedDecrement?.();
        e.preventDefault();
      }
    },
    [debouncedDecrement, debouncedIncrement],
  );

  return (
    <PopoverWrapper>
      <Popover placement={'bottom'} autoFocus={false} isLazy closeOnBlur closeOnEsc returnFocusOnClose>
        {/* STEPPERS + INPUT */}
        <Flex justifyContent={'center'} alignItems={'flex-end'} ref={dropdownRef} style={{ ...popOverStyleProps }}>
          {showInputStepper && (
            <NumberInputStepper
              type={StepperType.Decrement}
              onClick={debouncedDecrement}
              label={decrementLabel!}
              shortCutLabel={decrementShortCutLabel}
              stepperBtnStyles={stepperBtnStyles}
            />
          )}
          <Tooltip hasArrow placement={'bottom'} label={label} bg='black'>
            <Box>
              <PopoverTrigger>
                <Box as={'button'} data-testid={'number-input-btn-trigger'}>
                  <ToolbarNumberInput
                    value={value}
                    label={label}
                    precision={precision}
                    suffix={suffix}
                    max={max}
                    min={min}
                    hasBorder={popOverStyleProps.border !== 'none'}
                    onChange={handleChange}
                    inputTestId={inputTestId}
                    triggerBlurOnUnmount={triggerBlurOnUnmount}
                    numberInputFieldProps={{
                      borderRadius: showInputStepper ? 0 : 'base',
                      fontWeight: 'medium',
                      id: inputId,
                    }}
                    onKeyDown={handleKeyDown}
                    isMultipleFontSize={isMultipleFontSize}
                  />
                </Box>
              </PopoverTrigger>
            </Box>
          </Tooltip>
          {showInputStepper && (
            <NumberInputStepper
              type={StepperType.Increment}
              onClick={debouncedIncrement}
              label={incrementLabel!}
              shortCutLabel={incrementShortCutLabel}
              stepperBtnStyles={stepperBtnStyles}
            />
          )}
        </Flex>

        {/* DROPDOWN CONTENT */}
        <Box zIndex={'toolbarPopover'}>
          <PopoverContent w={'4rem'} boxShadow='md' padding='0' ref={contentRef}>
            <NumberInputDropdownContent
              options={dropdownOptions}
              onClick={handleSelect}
              selectedIndex={selectedIndex}
            />
          </PopoverContent>
        </Box>
      </Popover>
    </PopoverWrapper>
  );
};
